import React, { useEffect, Suspense, lazy, createElement } from 'react';
import { Hero } from './components/Hero';
const StatsAndTestimonials = lazy(() => import('./components/StatsAndTestimonials').then(m => ({
  default: m.StatsAndTestimonials
})));
const PainPoints = lazy(() => import('./components/PainPoints').then(m => ({
  default: m.PainPoints
})));
const Modules = lazy(() => import('./components/Modules').then(m => ({
  default: m.Modules
})));
const Bonuses = lazy(() => import('./components/Bonuses').then(m => ({
  default: m.Bonuses
})));
const IsForYou = lazy(() => import('./components/IsForYou').then(m => ({
  default: m.IsForYou
})));
const Pricing = lazy(() => import('./components/Pricing').then(m => ({
  default: m.Pricing
})));
const CostComparison = lazy(() => import('./components/CostComparison').then(m => ({
  default: m.CostComparison
})));
const FinalCTA = lazy(() => import('./components/FinalCTA').then(m => ({
  default: m.FinalCTA
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const PurchaseToast = lazy(() => import('./components/PurchaseToast').then(m => ({
  default: m.PurchaseToast
})));
export function App() {
  useEffect(() => {
    document.documentElement.lang = 'es-AR';
    document.title = 'Sistema Maestro de Aluminio™ — Todo tu taller de aluminio. Listo. Desde hoy.';
    const preconnects = [{
      href: 'https://cdn.magicpatterns.com',
      crossorigin: false
    }, {
      href: 'https://fonts.googleapis.com',
      crossorigin: false
    }, {
      href: 'https://fonts.gstatic.com',
      crossorigin: true
    }];
    const preconnectElements: HTMLLinkElement[] = [];
    preconnects.forEach(({
      href,
      crossorigin
    }) => {
      if (!document.querySelector(`link[rel="preconnect"][href="${href}"]`)) {
        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = href;
        if (crossorigin) link.crossOrigin = '';
        document.head.insertBefore(link, document.head.firstChild);
        preconnectElements.push(link);
      }
    });
    const fontsUrl = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Oswald:wght@500;600;700&display=swap';
    const existingFontLink = document.querySelector(`link[href="${fontsUrl}"]`);
    let fontLink: HTMLLinkElement | null = null;
    if (!existingFontLink) {
      const preloadLink = document.createElement('link');
      preloadLink.rel = 'preload';
      preloadLink.as = 'style';
      preloadLink.href = fontsUrl;
      document.head.appendChild(preloadLink);
      fontLink = document.createElement('link');
      fontLink.rel = 'stylesheet';
      fontLink.href = fontsUrl;
      fontLink.media = 'print';
      fontLink.onload = function () {
        ;
        (this as HTMLLinkElement).media = 'all';
      };
      document.head.appendChild(fontLink);
      const noscript = document.createElement('noscript');
      const fallbackLink = document.createElement('link');
      fallbackLink.rel = 'stylesheet';
      fallbackLink.href = fontsUrl;
      noscript.appendChild(fallbackLink);
      document.head.appendChild(noscript);
    }
    const metaDesc = document.querySelector('meta[name="description"]') || document.createElement('meta');
    metaDesc.setAttribute('name', 'description');
    metaDesc.setAttribute('content', 'Sistema Maestro de Aluminio: El sistema N°1 para carpinteros y armadores de aluminio en Argentina. Tablas de descuento, planos, mecanizados y presupuestos.');
    if (!document.querySelector('meta[name="description"]')) document.head.appendChild(metaDesc);
    if (!document.querySelector('meta[name="viewport"]')) {
      const viewport = document.createElement('meta');
      viewport.setAttribute('name', 'viewport');
      viewport.setAttribute('content', 'width=device-width, initial-scale=1');
      document.head.appendChild(viewport);
    }
    const ogTags = [{
      property: 'og:title',
      content: 'Sistema Maestro de Aluminio™ — Todo tu taller de aluminio. Listo. Desde hoy.'
    }, {
      property: 'og:description',
      content: 'El sistema N°1 para carpinteros y armadores de aluminio en Argentina. Línea Herrero → Módena. Todas las tipologías.'
    }, {
      property: 'og:type',
      content: 'product'
    }, {
      property: 'og:image',
      content: 'https://cdn.magicpatterns.com/uploads/hfD1CruJ8XYnedNYQnq6SX/Diseno_sin_titulo_(3).png'
    }, {
      property: 'og:locale',
      content: 'es_AR'
    }];
    ogTags.forEach(({
      property,
      content
    }) => {
      const existing = document.querySelector(`meta[property="${property}"]`);
      if (!existing) {
        const meta = document.createElement('meta');
        meta.setAttribute('property', property);
        meta.setAttribute('content', content);
        document.head.appendChild(meta);
      }
    });
    const jsonLd = document.createElement('script');
    jsonLd.type = 'application/ld+json';
    jsonLd.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'Sistema Maestro de Aluminio™',
      description: 'El sistema N°1 para carpinteros y armadores de aluminio en Argentina. 6 Tomos + 6 Bonos.',
      image: 'https://cdn.magicpatterns.com/uploads/hfD1CruJ8XYnedNYQnq6SX/Diseno_sin_titulo_(3).png',
      brand: {
        '@type': 'Brand',
        name: 'Sistema Maestro de Aluminio'
      },
      offers: [{
        '@type': 'Offer',
        name: 'Sistema Maestro de Aluminio',
        price: '19990',
        priceCurrency: 'ARS',
        availability: 'https://schema.org/InStock',
        url: '#pricing'
      }, {
        '@type': 'Offer',
        name: 'Sistema Maestro + Fichas de Taller',
        price: '29990',
        priceCurrency: 'ARS',
        availability: 'https://schema.org/InStock',
        url: '#pricing'
      }],
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '312'
      }
    });
    document.head.appendChild(jsonLd);
    return () => {
      jsonLd.remove();
      preconnectElements.forEach(el => el.remove());
      if (fontLink) fontLink.remove();
    };
  }, []);
  return <div className="min-h-screen bg-background text-gray-300 font-body selection:bg-gold selection:text-background" data-id="element-0">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-gold focus:text-background focus:px-4 focus:py-2 focus:rounded-lg focus:font-bold" data-id="element-1">
        Saltar al contenido principal
      </a>
      <main id="main-content" data-id="element-2">
        <Hero data-id="element-3" />
        <Suspense fallback={null} data-id="element-4">
          <StatsAndTestimonials data-id="element-5" />
          <PainPoints data-id="element-6" />
          <Modules data-id="element-7" />
          <Bonuses data-id="element-8" />
          <IsForYou data-id="element-9" />
          <Pricing data-id="element-10" />
          <CostComparison data-id="element-11" />
          <FinalCTA data-id="element-12" />
        </Suspense>
      </main>
      <Suspense fallback={null} data-id="element-13">
        <Footer data-id="element-14" />
        <PurchaseToast data-id="element-15" />
      </Suspense>
    </div>;
}